import { captureException } from '@sentry/node';
import { stringify } from 'qs';

import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { IShopCategory } from '~/src/api/content/shop/getShopCategories';
import contentRequest from '~/src/api/requests/contentRequest';
import { IPartnerVasContents } from '~/src/components/Promo/popups/PartnerPopup/types';
import { getAccessModeByRights } from '~/src/helpers/accessMode';
import { retryIfParamIsLocked } from '~/src/helpers/functions';

// @todo All types must be in api-domru package, now collected at frontend/packages/api-domru/src/api-content/common-types/shop-item.types.ts
// @deprecated use @r1-frontend/shared/types
export enum EShopCategory {
    Antiviruses = 'antiviruses',
    SpeedBonuses = 'speed-bonuses',
    Routers = 'routers',
    Intercom = 'intercom',
    Sale = 'special-offers',
    PartnerServices = 'partner-service-with-phone-check',
    Decoders = 'decoders',
    BonusToAll = 'bonus-to-all',
    YandexStation = 'yandex-stanciya',
    YandexStationLite = 'yandex-stanciya-lite',
    SafeKids = 'safe-kids',
    LoyaltyProgram = 'loyalty-program',
    Products = 'products',
    Subscriptions = 'subscriptions',
    Multibundle = 'multibundle',
    Videocontrol = 'videocontrol',
    PrivateSectorEquipment = 'private-sector-equipment',
}

export enum EExpandShopItems {
    Advertising = 'advertising',
    Category = 'category',
    Props = 'props',
    Labels = 'labels',
    CategoryProps = 'category.props',
}

interface IFilter {
    [key: string]: string | number,
}

export interface IShopLabel {
    id: number,
    name: string,
    title: string,
    color: string,
    color_text: string,
}

export interface IShopProp {
    name: string,
    title: string,
    type: string,
    value?: string | string[],
    computedValue?: string,
    asIs?: number,
}

export interface ICostRa {
    dur_type_id: number,
    duration: number,
    flag_ra: number,
    cost: number,
}

export interface IShopPriceOptions {
    label: string,
    price: number,
    pricePromo?: number,
    type: EPriceTypes.Possess | EPriceTypes.Leasing,
    operType: string,
}

export interface IShopPriceV2 {
    sort: number,
    type: number,
    date_close_promo: string | null,
    duration: number | null,
    id: number,
    lifetime_price: number | null,
    owner_id: number,
    price_type: string,
    value: number,
}

export interface IUrl {
    id: number,
    manual: number,
    name: string,
    owner_id: number,
    type: string,
    url: string,
}

export interface IAdvertising {
    companyName: string,
    erid: string,
}

export interface IShopItem {
    advertising: IAdvertising | null,
    alias: string,
    body: string,
    body_full: string,
    button_link: string,
    button_text: string,
    category_id: number,
    exists: boolean,
    extra: unknown[],
    global_id: number,
    id: number,
    image: string,
    image_webp?: string,
    images: string[],
    costRa: ICostRa[],
    // 0 или 1
    is_sale: number,
    prices: IShopItemPrices,
    pricesV2: IShopPriceV2[],
    pricesPromo: IShopItemPrices,
    sort: null | number,
    title: string,
    title_short?: string,
    labels?: IShopLabel[],
    category?: IShopCategory,
    image_widgets?: string,
    image_widgets_webp?: string,
    image_widgets_background?: string,
    image_background?: string,
    image_background_webp?: string,
    image_for_tariff_card: string,
    image_for_tariff_card_webp?: string,
    image_for_tariff_card_height: number,
    image_for_tariff_card_width: number,
    props?: IShopProp[],
    priceOptions?: IShopPriceOptions[],
    partnerVasContents?: IPartnerVasContents,
    image_width?: number,
    image_height?: number,
    leftovers: number | null,
    showForDelivery?: 0 | 1 | null,
    checkProductId?: number,
    name: string,
    urls?: IUrl[],
}

interface IShopItemParams {
    all: boolean,
    expand?: string | undefined,
    filter?: IFilter | undefined,
    category?: EShopCategory | string | null,
    accessMode?: number,
    providerId: number,
}

interface IMonth {
    value: number,
    lifetime_price: number,
}

interface IRent {
    month: IMonth | null,
    year: number | null,
}

export interface IShopItemPrices {
    leasing?: ILeasing[],
    possess?: number,
    rent?: IRent,
}

export interface ILeasing {
    price: number,
    duration: number,
}
/**
 * @deprecated use api-domru/src/api-content/v2/shop/items/index.ts
 */

export async function getShopItems(
    providerId: number,
    token: string | null,
    category: EShopCategory | string | null,
    expand?: EExpandShopItems[],
    filter?: IFilter | null,
    isShowAll = false,
): Promise<IShopItem[]> {

    const params: IShopItemParams = {
        providerId,
        ...filter,
        expand: prepareExpandProp(expand),
        all: isShowAll,
    };

    const headers: Record<string, any> = token
        ? { Authorization: `Bearer ${token}` }
        : {};

    if (providerId) {
        params.providerId = providerId;
    }

    if (category) {
        params.category = category;
    }

    params.accessMode = getAccessModeByRights({
        isAuth: Boolean(token),
    });

    const url = '/v1/shop/items';

    try {
        const resp = await retryIfParamIsLocked(
            () => contentRequest.get(url, {
                headers,
                params,
                paramsSerializer: (params: IShopItemParams) => {
                    return stringify(params);
                },
            }),
        );

        return resp.data.items;

    } catch (e) {
        captureException(e);
        return [];
    }
}

export async function getBonusToAll(
    providerId: number,
    token: string | null,
    expand?: EExpandShopItems[],
    filter?: IFilter,
): Promise<IShopItem[]> {

    let expandDefault = [EExpandShopItems.Category];
    if (expand) {
        expandDefault = [...expandDefault, ...expand];
    }
    return await getShopItems(providerId, token, EShopCategory.BonusToAll, expandDefault, filter);
}

function prepareExpandProp(expands: EExpandShopItems[] | undefined): string | undefined {
    if (expands && expands.length) {
        return expands.join(',');
    }
    return undefined;
}

export async function getRouters(providerId: number, token: string | null): Promise<IShopItem[]> {
    return getShopItems(
        providerId,
        token,
        EShopCategory.Routers,
        [EExpandShopItems.Category, EExpandShopItems.Props, EExpandShopItems.Labels],
    );
}

// eslint-disable-next-line no-undef
export async function getIntercomServices(providerId: number, token: string): Promise<unknown> {
    return getShopItems(
        providerId,
        token,
        EShopCategory.Intercom,
        [EExpandShopItems.Category, EExpandShopItems.Props, EExpandShopItems.Labels],
    );
}

export async function getVideocontrolItems(providerId: number, token: string): Promise<IShopItem[]> {
    return getShopItems(
        providerId,
        token,
        EShopCategory.Videocontrol,
        [EExpandShopItems.Category, EExpandShopItems.Props],
    );
}
